import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { AlertTriangle, ChevronRight, ArrowLeft, Cpu, Wifi, MapPin, User, Phone, Mail, Clock, ShieldAlert, FileText, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import TechnicalGuide from "@/components/TechnicalGuide";

const IMPACT_LABELS = { 0: "No Impact", 1: "Impact on Production", 2: "Impact on Safety", 3: "Impact on Production & Safety" };
const LEVEL_META = {
    0: { label: "Info", color: "#6B7280" },
    1: { label: "Warning", color: "#F97316" },
    2: { label: "Critical", color: "#DC2626" },
};

export default function AlertDetail() {
    const { alertId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [hover, setHover] = useState(null);

    useEffect(() => {
        api.get(`/alerts/${alertId}`).then(r => setData(r.data)).catch(e => setErr(formatErr(e.response?.data?.detail) || "Failed to load alert"));
    }, [alertId]);

    if (err) return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="alert-not-found">
            <ShieldAlert className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
            <div className="text-sm font-medium text-[#111827]">{err}</div>
            <Link to="/alerts" className="text-xs text-[#0A0A0A] font-medium mt-3 inline-block">← Back to alerts</Link>
        </div>
    );
    if (!data) return <div className="text-sm text-[#6B7280]">Loading alert…</div>;

    const { alert, plant, inverter, data_logger, station_data_loggers, power_history } = data;
    const level = LEVEL_META[alert.level] || LEVEL_META[1];
    const isOpen = alert.status === 1;
    const startTs = alert.alertStartTime * 1000;
    const startDate = new Date(startTs);
    const endTs = alert.alertEndTime ? alert.alertEndTime * 1000 : null;
    const durationMs = (endTs || Date.now()) - startTs;
    const durationHr = Math.floor(durationMs / 3600000);
    const durationMin = Math.floor((durationMs % 3600000) / 60000);

    // Prepare chart data with hover-friendly format (timestamps are epoch seconds → *1000; backend now emits true kW)
    const chartData = (power_history || []).map(d => {
        const ts = typeof d.timestamp === "number" ? d.timestamp * 1000 : new Date(d.timestamp).getTime();
        return {
            ts,
            time: new Date(ts).toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit", hour12: false }),
            kW: d.power_kw || 0,
        };
    });

    // 3-hour interval ticks that exist in the data
    const desiredHours = new Set(["00", "03", "06", "09", "12", "15", "18", "21"]);
    const alertChartTicks = [...new Set(
        chartData.filter(d => desiredHours.has(d.time.slice(0, 2)) && d.time.slice(3, 5) === "00").map(d => d.time)
    )];

    return (
        <div className="space-y-6" data-testid="alert-detail-page">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                <Link to="/alerts" className="hover:text-[#0A0A0A]">Alerts</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#111827] font-mono">{alert.protocolName || alert.alertCode}</span>
            </div>

            {/* Alert header banner */}
            <div
                className="rounded-lg overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${level.color}12 0%, ${level.color}04 100%)`, border: `1px solid ${level.color}33` }}
                data-testid="alert-banner"
            >
                <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${level.color}20`, border: `1px solid ${level.color}55` }}>
                            <AlertTriangle className="w-6 h-6" style={{ color: level.color }} />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: `${level.color}20`, color: level.color, border: `1px solid ${level.color}55` }}>
                                    {alert.protocolName || alert.alertCode}
                                </span>
                                <span className="text-xs font-mono uppercase tracking-widest" style={{ color: level.color }}>{level.label}</span>
                                <span className={`text-xs font-mono uppercase tracking-widest ${isOpen ? "text-[#DC2626]" : "text-[#16A34A]"}`}>
                                    · {isOpen ? "ACTIVE" : "CLEARED"}
                                </span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900">{alert.alertName}</h1>
                            <div className="text-sm text-[#6B7280] mt-1">{alert.description || alert.reason}</div>
                        </div>
                    </div>
                    <Button onClick={() => nav(`/plants/${plant.id}`)} variant="outline" className="rounded-md h-9 border-[#E5E7EB]" data-testid="alert-view-plant">
                        <FileText className="w-4 h-4 mr-2" /> View Plant
                    </Button>
                </div>
            </div>

            {/* Off-hours advisory banner — inverter's normal night shutdown, not a real fault */}
            {alert.is_operational_hours === false && (
                <div className="flex items-start gap-3 rounded-lg border border-[#6B7280]/30 bg-[#0B0D11]/[0.03] p-4" data-testid="off-hours-banner">
                    <Moon className="w-5 h-5 text-[#6B7280] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#374151] leading-relaxed">
                        <b className="text-[#111827]">This alert occurred outside solar operating hours</b> (05:30 – 18:00 IST). On-grid inverters normally shut down at sunset, so faults raised at night are usually just the inverter powering off cleanly — not a service action. Cross-check against the error code once available.
                    </div>
                </div>
            )}

            {/* Key facts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="alert-facts">
                <Fact icon={Clock} label="Started" value={startDate.toLocaleString()} />
                <Fact icon={Clock} label="Duration" value={endTs ? `${durationHr}h ${durationMin}m` : `${durationHr}h ${durationMin}m · ongoing`} accent={isOpen ? "#DC2626" : undefined} />
                <Fact icon={ShieldAlert} label="Impact" value={IMPACT_LABELS[alert.impact] || "Unknown"} accent={alert.impact >= 2 ? "#DC2626" : "#0A0A0A"} />
                <Fact icon={Cpu} label="Device SN" value={alert.deviceSn || "—"} mono />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Correlation chart */}
                <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg" data-testid="alert-chart">
                    <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Production Around Fault</div>
                            <div className="text-sm text-[#111827] mt-0.5">Last 24h · scroll or hover to inspect values</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">
                                {hover ? hover.time : "current"}
                            </div>
                            <div className="text-2xl font-mono font-medium text-[#0A0A0A] leading-tight">
                                {(hover ? hover.kW : chartData[chartData.length - 1]?.kW || 0).toFixed(1)} <span className="text-xs text-[#6B7280]">kW</span>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} onMouseMove={(s) => s?.activePayload?.[0] && setHover(s.activePayload[0].payload)} onMouseLeave={() => setHover(null)}>
                                <defs>
                                    <linearGradient id="g_alert" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#16A34A" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis
                                    dataKey="time"
                                    ticks={alertChartTicks}
                                    interval={0}
                                    tick={{ fontSize: 11, fill: "#6B7280", fontFamily: "IBM Plex Mono" }}
                                />
                                <YAxis
                                    domain={[0, 'auto']}
                                    tickFormatter={(v) => v.toFixed(1)}
                                    tick={{ fontSize: 11, fill: "#6B7280", fontFamily: "IBM Plex Mono" }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12, fontFamily: "Outfit" }}
                                    labelStyle={{ color: "#6B7280", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
                                    formatter={(v) => [`${v.toFixed(2)} kW`, "Power"]}
                                    cursor={{ stroke: "#0A0A0A", strokeWidth: 1, strokeDasharray: "3 3" }}
                                />
                                {/* Fault marker line */}
                                {chartData.length > 0 && (
                                    <ReferenceLine
                                        x={chartData.reduce((closest, d) => Math.abs(d.ts - startTs) < Math.abs(closest.ts - startTs) ? d : closest, chartData[0]).time}
                                        stroke="#DC2626" strokeDasharray="4 4"
                                        label={{ value: "fault", position: "top", fill: "#DC2626", fontSize: 10 }}
                                    />
                                )}
                                <Area type="monotone" dataKey="kW" stroke="#16A34A" strokeWidth={2} fill="url(#g_alert)" activeDot={{ r: 5, fill: "#0A0A0A", stroke: "#fff", strokeWidth: 2 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Context sidebar */}
                <div className="space-y-4">
                    {/* Plant */}
                    <Panel title="Plant" testid="alert-plant-panel">
                        <Link to={`/plants/${plant.id}`} className="text-sm font-semibold text-[#111827] hover:text-[#0A0A0A] block">{plant.customer_name || plant.name}</Link>
                        {plant.customer_name && (
                            <div className="text-[10px] font-mono text-[#9CA3AF] mt-0.5">{plant.name}</div>
                        )}
                        <div className="mt-1.5 text-xs text-[#6B7280] flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>{plant.address}</span></div>
                        {plant.owner_name && (
                            <div className="mt-3 pt-3 border-t border-[#F3F4F6] space-y-1.5">
                                <div className="text-xs text-[#6B7280] flex items-center gap-1.5"><User className="w-3 h-3" />{plant.owner_name}</div>
                                {plant.contact_phone && <div className="text-xs text-[#6B7280] flex items-center gap-1.5"><Phone className="w-3 h-3" />{plant.contact_phone}</div>}
                                {plant.owner_email && <div className="text-xs text-[#6B7280] flex items-center gap-1.5"><Mail className="w-3 h-3" /><a href={`mailto:${plant.owner_email}`} className="hover:text-[#0A0A0A]">{plant.owner_email}</a></div>}
                            </div>
                        )}
                    </Panel>

                    {/* Inverter (with Data Logger as sub-component) */}
                    <Panel title="Inverter" testid="alert-inverter-panel">
                        {inverter ? (
                            <>
                                <div className="flex items-center gap-2 mb-1">
                                    <Cpu className="w-3.5 h-3.5 text-[#0A0A0A]" />
                                    <span className="text-sm text-[#111827] font-medium">{inverter.model || "Inverter"}</span>
                                </div>
                                <div className="text-xs font-mono text-[#6B7280]">SN {inverter.serial_number}</div>
                                {inverter.rated_kw != null && (
                                    <div className="text-xs font-mono text-[#6B7280] mt-0.5">Rated {inverter.rated_kw.toFixed(1)} kW</div>
                                )}
                            </>
                        ) : data_logger ? (
                            <div className="text-xs text-[#6B7280]">
                                Alert originated from the data logger. See sub-component below.
                            </div>
                        ) : (
                            <div className="text-xs text-[#6B7280]">Device not matched in station registry.</div>
                        )}
                        {/* Data logger as sub-component */}
                        {(station_data_loggers || []).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-dashed border-[#E5E7EB]" data-testid="alert-logger-sub">
                                <div className="text-[9px] font-semibold uppercase tracking-widest text-[#9CA3AF] mb-1.5">Data Logger · sub-component</div>
                                {station_data_loggers.map((l) => (
                                    <div key={l.serial_number} className="flex items-center gap-2 text-xs">
                                        <Wifi className={`w-3 h-3 ${l.state === 1 ? "text-[#16A34A]" : "text-[#0A0A0A]"}`} />
                                        <span className="text-[#111827] truncate">{l.model}</span>
                                        <span className="font-mono text-[#6B7280] ml-auto">{l.serial_number}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Panel>
                </div>
            </div>

            {/* Diagnosis + solution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="alert-reason">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Reason</div>
                    <div className="text-sm text-[#111827] leading-relaxed">{alert.reason || alert.description || "No description provided by SolarOS."}</div>
                </div>
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="alert-solution">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Recommended Action</div>
                    <div className="text-sm text-[#111827] leading-relaxed">{alert.solution || "Contact RPPL service team for investigation."}</div>
                </div>
            </div>

            {/* Manual-based Technical Guide (F## / ERR-### / WARN-### mapped to RPPL inverter A3 manuals) */}
            <TechnicalGuide guide={alert.technical_guide} testid="alert-detail-guide" />
        </div>
    );
}

function Fact({ icon: Icon, label, value, accent, mono }) {
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: accent || "#6B7280" }} />
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">{label}</div>
            </div>
            <div className={`mt-1.5 text-sm ${mono ? "font-mono" : ""}`} style={{ color: accent || "#111827" }}>{value}</div>
        </div>
    );
}

function Panel({ title, children, testid }) {
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg" data-testid={testid}>
            <div className="p-3 border-b border-[#F3F4F6]">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">{title}</div>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}
